import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box, type IconType, TooltipWrapper } from "@memorang/ui";
import { useTranslation } from "react-i18next";
import { Pressable, type ViewStyle } from "react-native";
import { Text } from "react-native-paper";

export type ModuleStatusFilter = "all" | "ACTIVE" | "NOT_STARTED" | "COMPLETE";

type FilterButton = {
	value: ModuleStatusFilter;
	label: string;
	icon: IconType;
};

type ModuleStatusFilterButtonsProps = {
	value: ModuleStatusFilter;
	onValueChange: (value: ModuleStatusFilter) => void;
	hasAccess: boolean;
};

const FILTER_BUTTONS: FilterButton[] = [
	{
		value: "all",
		label: "All",
		icon: "view-grid-plus-outline",
	},
	{
		value: "ACTIVE",
		label: "In progress",
		icon: "progress-clock",
	},
	{
		value: "NOT_STARTED",
		label: "Not started",
		icon: "play-circle-outline",
	},
	{
		value: "COMPLETE",
		label: "Completed",
		icon: "check-circle-outline",
	},
];

type FilterSegmentProps = {
	button: FilterButton;
	index: number;
	selected: boolean;
	disabled: boolean;
	onPress: () => void;
};

const FilterSegment = ({
	button,
	index,
	selected,
	disabled,
	onPress,
}: FilterSegmentProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const segmentStyle = {
		flex: 1,
		flexDirection: "row" as const,
		alignItems: "center" as const,
		justifyContent: "center" as const,
		gap: 6,
		minHeight: 36,
		paddingHorizontal: 8,
		backgroundColor: selected
			? theme.colors.secondaryContainer
			: theme.colors.surface,
		opacity: disabled ? 0.6 : 1,
		borderLeftWidth: index === 0 ? 0 : 1,
		borderLeftColor: theme.colors.outline,
		// @ts-ignore web-specific affordance
		cursor: disabled ? "not-allowed" : "pointer",
	};

	const segment = (
		<Pressable
			disabled={disabled}
			onPress={onPress}
			accessibilityRole="button"
			accessibilityState={{ selected, disabled }}
			style={segmentStyle as ViewStyle}
		>
			<Icon
				name={button.icon}
				size={16}
				color={selected ? theme.colors.primary : theme.colors.onSurface}
			/>
			<Text
				variant="labelLarge"
				style={{
					fontSize: 12,
					color: selected ? theme.colors.primary : theme.colors.onSurface,
				}}
			>
				{button.label}
			</Text>
		</Pressable>
	);

	if (disabled && isWeb) {
		return (
			<TooltipWrapper
				title={t("premium.unlockFiltersTooltip")}
				style={{
					flex: 1,
					display: "flex",
					cursor: "not-allowed",
				}}
			>
				{segment}
			</TooltipWrapper>
		);
	}

	return <Box style={{ flex: 1 }}>{segment}</Box>;
};

export const ModuleStatusFilterButtons = ({
	value,
	onValueChange,
	hasAccess,
}: ModuleStatusFilterButtonsProps) => {
	const theme = useAppTheme();

	const handlePress = (filterValue: ModuleStatusFilter) => {
		if (hasAccess || filterValue === "all") {
			onValueChange(filterValue);
		}
	};

	return (
		<Box
			flexDirection="row"
			width="100%"
			style={{
				borderWidth: 1,
				borderColor: theme.colors.outline,
				borderRadius: theme.roundness,
				overflow: "hidden",
				backgroundColor: theme.colors.surface,
			}}
		>
			{FILTER_BUTTONS.map((button, index) => (
				<FilterSegment
					key={button.value}
					button={button}
					index={index}
					selected={value === button.value}
					disabled={!hasAccess && button.value !== "all"}
					onPress={() => handlePress(button.value)}
				/>
			))}
		</Box>
	);
};
